import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BaseTableComponent } from '../../../../shared/tables/base-table/base-table';
import { BellScheduleResponseDto } from '@whetstoneeducation/hero-common';
import { BellSchedulesListTableColumns } from '../../bell-schedules-models/bell-schedules-list-table-columns.enum';
import { MatDialog } from '@angular/material/dialog';
import { AppConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { AppBellSchedulesService } from '../../bell-schedules.service';
import { AppToastManagerService } from 'src/app/shared/services/toast-manager.service';

@Component({
  selector: 'app-bell-schedules-list-table',
  templateUrl: './bell-schedules-list-table.template.html',
  styleUrls: ['./bell-schedules-list-table.scss']
})
export class AppBellSchedulesListTableComponent extends BaseTableComponent<BellScheduleResponseDto> {
  @Input() showSelectOptions: boolean;

  public get displayedColumns() {
    const columns = Object.values(BellSchedulesListTableColumns);
    if (!this.showSelectOptions) {
      columns.shift();
    }
    return columns;
  }

  public columnValues = {
    [BellSchedulesListTableColumns.NAME]: ['name'],
    [BellSchedulesListTableColumns.DESCRIPTION]: ['description']
  };

  public columns = BellSchedulesListTableColumns;

  @Input()
  public selectedBellSchedules: BellScheduleResponseDto[] = [];

  @Output() updateTable = new EventEmitter<void>();

  @Output()
  public selectedBellSchedulesChanged: EventEmitter<BellScheduleResponseDto[]>;

  @Output()
  public bellScheduleSelected: EventEmitter<BellScheduleResponseDto>;

  constructor(
    public dialog: MatDialog,
    private bellSchedulesService: AppBellSchedulesService,
    public toastService: AppToastManagerService,
  ) {
    super();
    this.selectedBellSchedulesChanged = new EventEmitter<
      BellScheduleResponseDto[]
    >();
    this.bellScheduleSelected = new EventEmitter<BellScheduleResponseDto>();
  }

  public selectAllBellSchedules(): void {
    const allSelected =
      this.dataArr.length === this.selectedBellSchedules.length;
    if (allSelected) {
      this.selectedBellSchedules = [];
    } else {
      this.selectedBellSchedules = this.dataArr;
    }
    this.selectedBellSchedulesChanged.emit(this.selectedBellSchedules);
  }

  public selectBellSchedule(
    selectedBellSchedule: BellScheduleResponseDto
  ): void {
    const isSelected = !!this.selectedBellSchedules.find(
      (bellSchedule) => bellSchedule.id === selectedBellSchedule.id
    );
    if (isSelected) {
      this.selectedBellSchedules = this.selectedBellSchedules.filter(
        (bellSchedule) => bellSchedule.id !== selectedBellSchedule.id
      );
    } else {
      this.selectedBellSchedules.push(selectedBellSchedule);
    }
    this.selectedBellSchedulesChanged.emit(this.selectedBellSchedules);
  }

  public isSelected(bellScheduleId: number): boolean {
    return !!this.selectedBellSchedules.find(
      (bellSchedule) => bellSchedule.id === bellScheduleId
    );
  }

  public async activateOrDeactivateBellSchedule(bellSchedule: BellScheduleResponseDto, deactivate: boolean): Promise<void> {
    try {
      await this.bellSchedulesService.deactivateOrReactivateBellSchedule(bellSchedule.id, deactivate);
      this.updateTable.emit()
    } catch (error) {
      bellSchedule.active = false;
      this.toastService.error(`Error ${deactivate? 'de': 're'}activating bell schedule!`);
    }
  }

  public async confirmDeactivateBellSchedule(bellSchedule: BellScheduleResponseDto): Promise<void> {
    const bellScheduleInUse = await this.bellSchedulesService.isBellScheduleInUse(bellSchedule.id);
    const title = bellScheduleInUse.isInUse ? 'Bell Schedule in Use' : `Deactivate ${bellSchedule.name}`;
    const content = bellScheduleInUse.isInUse ? `This bell schedule is currently in use. Deactivating it will remove it from all associated settings. Please ensure to set up all necessary bell schedules.
Are you sure you want to deactivate this bell schedule?` : 'Are you sure you want to deactivate this bell schedule?';
    this.dialog.open(AppConfirmationDialogComponent, {
      width: '40rem',
      height: 'auto',
      autoFocus: false,

      data: {
        title,
        content,
        actions: [
          {
            key: 'cancel',
            label: 'Cancel',
            color: 'secondary',
            classes: 'outline-button'
          },
          {
            key: 'deactivate',
            label: 'Yes, Deactivate',
            color: 'primary',
            classes: 'modal-delete-button',
            awaitAction: true,
            action: async () => {
              this.activateOrDeactivateBellSchedule(bellSchedule, true)
            }
          },
        ]
      }
    });
  }
}
